'use client';

import React, { useState } from 'react';
import { Search, TrendingUp, TrendingDown, Minus, Target, AlertCircle } from 'lucide-react';
import { MOCK_FCR_RECORDS, FCRRecord } from './feedData';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Cell } from 'recharts';

const STATUS_STYLES: Record<string, string> = {
  Excellent: 'bg-emerald-100 text-emerald-700',
  Good: 'bg-blue-100 text-blue-700',
  Average: 'bg-amber-100 text-amber-700',
  Poor: 'bg-red-100 text-red-700',
};

const FCR_COLORS: Record<string, string> = {
  Excellent: '#10b981',
  Good: '#3b82f6',
  Average: '#f59e0b',
  Poor: '#ef4444',
};

function VarianceIcon({ variance }: { variance: number }) {
  if (variance < -0.01) return <TrendingDown size={14} className="text-emerald-500" />;
  if (variance > 0.01) return <TrendingUp size={14} className="text-red-500" />;
  return <Minus size={14} className="text-gray-400" />;
}

export default function FCRAnalyticsTab() {
  const [records] = useState<FCRRecord[]>(MOCK_FCR_RECORDS);
  const [search, setSearch] = useState('');
  const [filterFarm, setFilterFarm] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const farms = Array.from(new Set(records.map(r => r.farm)));

  const filtered = records.filter(r => {
    const q = search.toLowerCase();
    const matchSearch = !q || r.flockName.toLowerCase().includes(q) || r.house.toLowerCase().includes(q);
    const matchFarm = filterFarm === 'all' || r.farm === filterFarm;
    const matchStatus = filterStatus === 'all' || r.status === filterStatus;
    return matchSearch && matchFarm && matchStatus;
  });

  const broilerRecords = filtered.filter(r => r.fcr > 0);
  const avgFCR = broilerRecords.length > 0 ? (broilerRecords.reduce((s, r) => s + r.fcr, 0) / broilerRecords.length).toFixed(2) : '—';
  const bestFCR = broilerRecords.length > 0 ? Math.min(...broilerRecords.map(r => r.fcr)).toFixed(2) : '—';
  const totalFeedCost = filtered.reduce((s, r) => s + r.totalFeedCost, 0);

  const chartData = filtered.filter(r => r.fcr > 0).map(r => ({
    name: r.flockName.replace('Broiler Batch ', 'Batch ').replace('Layer Flock ', 'Layer '),
    fcr: r.fcr,
    target: r.targetFcr,
    status: r.status,
  }));

  return (
    <div>
      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        {[
          { label: 'Avg FCR (Broilers)', value: avgFCR, color: 'text-indigo-600', sub: 'lower is better' },
          { label: 'Best FCR', value: bestFCR, color: 'text-emerald-600', sub: 'top performer' },
          { label: 'Total Feed Cost', value: `UGX ${(totalFeedCost / 1000000).toFixed(1)}M`, color: 'text-gray-700', sub: 'all flocks' },
          { label: 'Flocks Tracked', value: filtered.length, color: 'text-purple-600', sub: 'this period' },
        ].map(k => (
          <div key={k.label} className="p-4 rounded-xl border border-gray-100" style={{ backgroundColor: 'var(--card-bg)' }}>
            <div className="text-xs text-gray-500 mb-1">{k.label}</div>
            <div className={`text-xl font-bold ${k.color}`}>{k.value}</div>
            <div className="text-xs text-gray-400 mt-0.5">{k.sub}</div>
          </div>
        ))}
      </div>

      {/* FCR Chart */}
      {chartData.length > 0 && (
        <div className="p-5 rounded-xl border border-gray-100 mb-5" style={{ backgroundColor: 'var(--card-bg)' }}>
          <div className="flex items-center gap-2 mb-4">
            <Target size={15} className="text-indigo-500" />
            <h3 className="text-sm font-semibold text-gray-700">FCR vs Target by Flock</h3>
            <span className="text-xs text-gray-400 ml-auto">Lower FCR = Better feed efficiency</span>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis domain={[0, 3]} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(val: number) => val.toFixed(2)} />
              <ReferenceLine y={2.0} stroke="#f59e0b" strokeDasharray="4 4" label={{ value: 'Avg Target', fontSize: 10, fill: '#f59e0b' }} />
              <Bar dataKey="fcr" radius={[4, 4, 0, 0]} name="Actual FCR">
                {chartData.map((entry, i) => (
                  <Cell key={i} fill={FCR_COLORS[entry.status] ?? '#6366f1'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-3 mt-3 justify-center">
            {Object.entries(FCR_COLORS).map(([label, color]) => (
              <div key={label} className="flex items-center gap-1.5 text-xs text-gray-500">
                <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: color }} />
                {label}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search FCR records…" className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300" style={{ backgroundColor: 'var(--card-bg)' }} />
        </div>
        <select value={filterFarm} onChange={e => setFilterFarm(e.target.value)} className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none" style={{ backgroundColor: 'var(--card-bg)' }}>
          <option value="all">All Farms</option>
          {farms.map(f => <option key={f} value={f}>{f}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none" style={{ backgroundColor: 'var(--card-bg)' }}>
          <option value="all">All Performance</option>
          <option value="Excellent">Excellent</option>
          <option value="Good">Good</option>
          <option value="Average">Average</option>
          <option value="Poor">Poor</option>
        </select>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-gray-100">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100" style={{ backgroundColor: 'var(--surface)' }}>
              {['Flock','House / Farm','Bird Type','Week','Period','Feed Consumed (kg)','Weight Gain (kg)','Actual FCR','Target FCR','Variance','Avg BW (kg)','Feed Cost/kg','Total Feed Cost','Performance'].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map(r => (
              <tr key={r.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors" style={{ backgroundColor: 'var(--card-bg)' }}>
                <td className="px-4 py-3 font-medium text-gray-800 whitespace-nowrap">{r.flockName}</td>
                <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                  <div className="text-xs">{r.house}</div>
                  <div className="text-xs text-gray-400">{r.farm}</div>
                </td>
                <td className="px-4 py-3 text-gray-600">{r.birdType}</td>
                <td className="px-4 py-3 text-center text-gray-600">Wk {r.weekNumber}</td>
                <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{r.periodStart} → {r.periodEnd}</td>
                <td className="px-4 py-3 text-right font-medium text-gray-700">{r.totalFeedConsumedKg.toLocaleString()}</td>
                <td className="px-4 py-3 text-right text-emerald-600 font-medium">{r.totalWeightGainKg > 0 ? r.totalWeightGainKg.toLocaleString() : '—'}</td>
                <td className="px-4 py-3 text-right">
                  {r.fcr > 0 ? (
                    <span className={`font-bold text-base ${r.fcr <= r.targetFcr ? 'text-emerald-600' : 'text-red-600'}`}>{r.fcr.toFixed(2)}</span>
                  ) : <span className="text-gray-400 text-xs">N/A (Layer)</span>}
                </td>
                <td className="px-4 py-3 text-right text-gray-500">{r.targetFcr > 0 ? r.targetFcr.toFixed(2) : '—'}</td>
                <td className="px-4 py-3 text-right">
                  {r.fcr > 0 ? (
                    <div className="flex items-center justify-end gap-1">
                      <VarianceIcon variance={r.fcrVariance} />
                      <span className={`text-xs font-medium ${r.fcrVariance < 0 ? 'text-emerald-600' : r.fcrVariance > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                        {r.fcrVariance > 0 ? '+' : ''}{r.fcrVariance.toFixed(2)}
                      </span>
                    </div>
                  ) : '—'}
                </td>
                <td className="px-4 py-3 text-right text-gray-600">{r.avgBodyWeightKg.toFixed(2)}</td>
                <td className="px-4 py-3 text-right text-gray-600">{r.feedCostPerKg.toLocaleString()}</td>
                <td className="px-4 py-3 text-right font-medium text-gray-700">{(r.totalFeedCost / 1000).toFixed(0)}K</td>
                <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${STATUS_STYLES[r.status]}`}>{r.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="py-12 text-center text-gray-400 text-sm">No FCR records found</div>}
      </div>

      {/* FCR Guide */}
      <div className="mt-5 p-4 rounded-xl border border-indigo-100 bg-indigo-50">
        <div className="flex items-center gap-2 mb-2">
          <AlertCircle size={14} className="text-indigo-500" />
          <span className="text-xs font-semibold text-indigo-700">FCR Reference Guide</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs text-gray-600">
          <div><span className="font-semibold text-emerald-600">Excellent:</span> FCR &lt; 1.6 (Broiler Wk 1–2)</div>
          <div><span className="font-semibold text-blue-600">Good:</span> FCR 1.6–1.9 (Broiler Wk 3–4)</div>
          <div><span className="font-semibold text-amber-600">Average:</span> FCR 1.9–2.3 (Broiler Wk 5–6)</div>
          <div><span className="font-semibold text-red-600">Poor:</span> FCR &gt; 2.3 — investigate feed waste or health issues</div>
        </div>
      </div>
    </div>
  );
}
